import { z } from "zod";
/**
 * Internal team chat — Zod SSOT.
 *
 * Canon: docs/enhancements/chat-2026-05.md (build plan), docs/
 * enhancements/feature-sketches-2026-05.md §8.
 *
 * The naming `messages` (Schema/Create/etc.) is preserved on the
 * outer route surface for v0 compatibility. Internally the table
 * is `chat_messages` and the canonical type name is `ChatMessage*`.
 * Old aliases re-export for the duration of Phase A.
 */
// ─── Enums ─────────────────────────────────────────────────
export const ChannelKindSchema = z.enum(["public", "private", "dm"]);
export const ChannelMemberRoleSchema = z.enum(["owner", "admin", "member"]);
export const NotificationLevelSchema = z.enum(["all", "mentions", "none"]);
export const ChatMessageKindSchema = z.enum(["user", "system"]);
export const NotificationReasonSchema = z.enum([
    "mention",
    "dm",
    "thread_reply",
    "channel_message",
]);
// ─── Mentions + attachments (write-time extracted) ─────────
export const ChatMentionSchema = z.object({
    kind: z.enum(["user", "channel", "contact", "company", "project", "deal"]),
    /** Resolved id (UUID for entities, email for users, channel id) or
     *  the raw token if unresolved. Render-time decides what to do. */
    target: z.string().min(1).max(320),
    display: z.string().min(1).max(200),
});
export const ChatAttachmentSchema = z.object({
    key: z.string().min(1).max(500),
    filename: z.string().min(1).max(300),
    contentType: z.string().min(1).max(120),
    size: z.number().int().min(0),
});
// ─── Channel ───────────────────────────────────────────────
export const ChannelSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(80),
    description: z.string().max(500).nullable(),
    kind: ChannelKindSchema,
    topic: z.string().max(250).nullable(),
    createdBy: z.string().email(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ChannelCreateSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(500).optional(),
    kind: ChannelKindSchema.default("public"),
    topic: z.string().max(250).optional(),
    memberEmails: z.array(z.string().email()).max(50).optional(),
});
export const ChannelUpdateSchema = z
    .object({
    name: z.string().min(1).max(80),
    description: z.string().max(500).nullable(),
    topic: z.string().max(250).nullable(),
})
    .partial();
// ─── Channel member ────────────────────────────────────────
export const ChannelMemberSchema = z.object({
    channelId: z.string().uuid(),
    userEmail: z.string().email(),
    role: ChannelMemberRoleSchema,
    notificationLevel: NotificationLevelSchema,
    mutedUntil: z.string().datetime().nullable(),
    lastReadAt: z.string().datetime().nullable(),
    joinedAt: z.string().datetime(),
});
export const ChannelMemberUpdateSchema = z
    .object({
    role: ChannelMemberRoleSchema,
    notificationLevel: NotificationLevelSchema,
    mutedUntil: z.string().datetime().nullable(),
})
    .partial();
// ─── Chat message ──────────────────────────────────────────
export const ChatMessageSchema = z.object({
    id: z.string().uuid(),
    channelId: z.string().uuid(),
    threadRootId: z.string().uuid().nullable(),
    authorEmail: z.string().email(),
    kind: ChatMessageKindSchema,
    body: z.string(),
    attachments: z.array(ChatAttachmentSchema),
    mentions: z.array(ChatMentionSchema),
    editedAt: z.string().datetime().nullable(),
    deletedAt: z.string().datetime().nullable(),
    deletedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
});
export const ChatMessageCreateSchema = z.object({
    channelId: z.string().uuid(),
    threadRootId: z.string().uuid().nullable().default(null),
    body: z.string().min(1).max(10_000),
    attachments: z.array(ChatAttachmentSchema).max(10).default([]),
});
export const ChatMessageUpdateSchema = z.object({
    body: z.string().min(1).max(10_000),
});
// ─── Listing / pagination ──────────────────────────────────
export const ChatMessageListQuerySchema = z.object({
    channelId: z.string().uuid(),
    /** When set, returns the replies under this thread root (excluding the
     *  root itself). Otherwise returns top-level channel posts. */
    threadRootId: z.string().uuid().optional(),
    before: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const ChannelListResponseSchema = z.object({
    channels: z.array(ChannelSchema),
});
// Forward declare ChatReactionSummary for the list response — defined
// fully below in the Reactions section but referenced here.
const ChatReactionSummaryForListSchema = z.object({
    emoji: z.string().min(1).max(32),
    count: z.number().int().min(0),
    users: z.array(z.string().email()),
    reactedByMe: z.boolean(),
});
export const ChatMessageListResponseSchema = z.object({
    messages: z.array(ChatMessageSchema),
    /** Per-message reaction summary keyed by message id. Messages with
     *  no reactions are absent (callers should default to []). Saves an
     *  N+1 round trip on the reactions table. */
    reactions: z.record(z.string().uuid(), z.array(ChatReactionSummaryForListSchema)),
    hasMore: z.boolean(),
});
// ─── Reactions (Phase C) ───────────────────────────────────
export const ChatReactionSchema = z.object({
    messageId: z.string().uuid(),
    userEmail: z.string().email(),
    /** Unicode emoji (e.g. "👍") or short-code without colons (e.g. "thumbsup").
     *  Stored verbatim; renderer decides how to display. */
    emoji: z.string().min(1).max(32),
    createdAt: z.string().datetime(),
});
export const ChatReactionCreateSchema = z.object({
    emoji: z.string().min(1).max(32),
});
/** Reaction summary for a message — what the inbox / message-list renders. */
export const ChatReactionSummarySchema = z.object({
    emoji: z.string().min(1).max(32),
    count: z.number().int().min(0),
    /** Emails of users who reacted with this emoji, capped to ~25 for list-render
     *  perf; the count above is authoritative. */
    users: z.array(z.string().email()),
    /** Whether the *calling* user has reacted with this emoji — drives the
     *  toggle UI. */
    reactedByMe: z.boolean(),
});
// ─── Cross-channel notification inbox (Phase C) ────────────
export const ChatNotificationSchema = z.object({
    id: z.string().uuid(),
    userEmail: z.string().email(),
    channelId: z.string().uuid(),
    messageId: z.string().uuid(),
    reason: NotificationReasonSchema,
    readAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
/** Inbox row enriched with the message + channel context the UI needs to
 *  render the row without follow-up fetches. */
export const ChatInboxItemSchema = z.object({
    notification: ChatNotificationSchema,
    channel: ChannelSchema,
    message: ChatMessageSchema,
});
export const ChatInboxResponseSchema = z.object({
    items: z.array(ChatInboxItemSchema),
    unreadCount: z.number().int().min(0),
});
// ─── Channel ↔ CRM links (Phase B) ─────────────────────────
export const ChannelLinkResourceKindSchema = z.enum(["contact", "company", "project", "deal"]);
export const ChannelLinkSchema = z.object({
    id: z.string().uuid(),
    channelId: z.string().uuid(),
    resourceKind: ChannelLinkResourceKindSchema,
    resourceId: z.string().uuid(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
});
export const ChannelLinkCreateSchema = z.object({
    resourceKind: ChannelLinkResourceKindSchema,
    resourceId: z.string().uuid(),
});
export const ChannelLinkListResponseSchema = z.object({
    links: z.array(ChannelLinkSchema),
});
export const ChannelsForResourceQuerySchema = z.object({
    resourceKind: ChannelLinkResourceKindSchema,
    resourceId: z.string().uuid(),
});
// ─── Attachments (Phase E) ─────────────────────────────────
/** Content types we accept for chat attachments. Widely-used formats
 *  cover ~all of what staff actually paste/drag. SVG is excluded to
 *  side-step the SVG-as-XSS-vector risk; text/* is accepted so logs
 *  and snippets render natively. */
export const CHAT_ATTACHMENT_ALLOWED_TYPES = [
    // Images (no SVG)
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    // Docs
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    // Plain
    "text/plain",
    "text/csv",
    "text/markdown",
    // Compressed
    "application/zip",
    "application/json",
];
/** 25 MB per attachment. Bumps require operator review (SAS upload
 *  has its own ceilings; Container App ingress has body-size limits). */
export const CHAT_ATTACHMENT_MAX_SIZE = 25 * 1024 * 1024;
export const ChatAttachmentUploadRequestSchema = z.object({
    filename: z.string().min(1).max(300),
    contentType: z.enum(CHAT_ATTACHMENT_ALLOWED_TYPES),
    size: z.number().int().min(1).max(CHAT_ATTACHMENT_MAX_SIZE),
    /** Channel the attachment will be posted into. Used for ACL +
     *  for the storage path, so the attachment lives under that
     *  channel's prefix (makes per-channel cleanup tractable). */
    channelId: z.string().uuid(),
});
export const ChatAttachmentUploadResponseSchema = z.object({
    /** Storage key — what the client puts on the `attachments` array of
     *  the message after the upload completes. */
    key: z.string().min(1),
    /** Signed PUT URL — short-TTL (15 min). Client uploads with
     *  `Content-Type: <contentType>` and `x-ms-blob-type: BlockBlob`. */
    uploadUrl: z.string().url(),
    /** ISO datetime — informational; the URL itself enforces expiry. */
    expiresAt: z.string().datetime(),
});
export const ChatAttachmentDownloadResponseSchema = z.object({
    downloadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
});
// ─── FTS search (Phase D) ──────────────────────────────────
export const ChatSearchQuerySchema = z.object({
    /** Free-text query — passed to `websearch_to_tsquery('english', q)`,
     *  so it supports `"phrase queries"`, `OR`, `-exclude`, etc. */
    q: z.string().min(1).max(200),
    /** Optional channel scope. If omitted, search across every channel
     *  the caller can read (member-of-it OR public). */
    channelId: z.string().uuid().optional(),
    /** Restrict to messages by this author. */
    authorEmail: z.string().email().optional(),
    /** ISO datetime — only return messages strictly before this. */
    before: z.string().datetime().optional(),
    /** ISO datetime — only return messages on/after this. */
    after: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const ChatSearchHitSchema = z.object({
    message: ChatMessageSchema,
    channel: ChannelSchema,
    /** Rank score after recency-decay weighting; higher = more relevant. */
    rank: z.number(),
});
export const ChatSearchResponseSchema = z.object({
    hits: z.array(ChatSearchHitSchema),
    total: z.number().int().min(0),
});
// ─── Mention autocomplete (Phase H) ────────────────────────
export const MentionSuggestPrefixSchema = z.enum(["@", "#"]);
export const MentionSuggestQuerySchema = z.object({
    /** Substring to match — typically the chars typed after `@` or `#`. */
    q: z.string().max(200).default(""),
    /** Source channel — narrows user-suggestions to this channel's
     *  members. Required because mentions are channel-scoped (Phase B). */
    channelId: z.string().uuid(),
    /** `@` returns users + CRM entities; `#` returns channels only. */
    prefix: MentionSuggestPrefixSchema.default("@"),
    limit: z.coerce.number().int().min(1).max(20).default(8),
});
export const MentionSuggestionSchema = z.object({
    kind: z.enum(["user", "channel", "contact", "company", "project"]),
    /** Resolved id (UUID) or email — what gets stored in mentions JSONB. */
    target: z.string().min(1).max(320),
    /** Primary label shown in the dropdown row. */
    display: z.string().min(1).max(200),
    /** Optional secondary label — e.g. company name for contacts, role
     *  for projects. Hint, not authoritative. */
    secondaryLabel: z.string().max(200).optional(),
});
export const MentionSuggestResponseSchema = z.object({
    suggestions: z.array(MentionSuggestionSchema),
});
// ─── Link unfurl (Phase H) ─────────────────────────────────
export const LinkUnfurlQuerySchema = z.object({
    url: z.string().url().max(2048),
});
export const LinkUnfurlResponseSchema = z.object({
    url: z.string().url(),
    title: z.string().max(500).nullable(),
    description: z.string().max(1000).nullable(),
    imageUrl: z.string().url().nullable(),
    siteName: z.string().max(200).nullable(),
});
// ─── Web push (Phase H follow-up) ──────────────────────────
/**
 * Browser-issued push subscription. The PushManager hands the SPA an
 * opaque endpoint URL plus two encryption keys; the SPA POSTs all
 * three to /messages/push/subscribe. Server upserts by endpoint.
 */
export const WebPushSubscribeSchema = z.object({
    /** Push service endpoint URL (Mozilla / FCM / Apple). */
    endpoint: z.string().url().max(2048),
    /** Auth + p256dh keys — browser-managed, opaque base64url blobs. */
    keys: z.object({
        auth: z.string().min(1).max(200),
        p256dh: z.string().min(1).max(200),
    }),
    /** Browser User-Agent for diagnostics + multi-device de-dupe.
     *  Bounded so a malicious client can't blow up the row. */
    userAgent: z.string().max(500).default(""),
});
export const WebPushUnsubscribeSchema = z.object({
    endpoint: z.string().url().max(2048),
});
/** Returned by GET /messages/push/vapid-public-key — used by the SPA
 *  during PushManager.subscribe(). Public by definition; safe to
 *  expose to authenticated callers. */
export const WebPushVapidKeyResponseSchema = z.object({
    publicKey: z.string().min(1).max(200),
});
// ─── Compliance (Phase G) ──────────────────────────────────
export const ChatComplianceUserParamSchema = z.object({
    userEmail: z.string().email(),
});
export const ChatTranscriptQuerySchema = z.object({
    userEmail: z.string().email().optional(),
    before: z.string().datetime().optional(),
    after: z.string().datetime().optional(),
});
// ─── WebSocket events ──────────────────────────────────────
export const WsMessageEventSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("message"), data: ChatMessageSchema }),
    z.object({ type: z.literal("message_updated"), data: ChatMessageSchema }),
    z.object({
        type: z.literal("message_deleted"),
        data: z.object({ messageId: z.string().uuid(), channelId: z.string().uuid() }),
    }),
    z.object({
        type: z.literal("typing"),
        data: z.object({ channelId: z.string().uuid(), userEmail: z.string().email() }),
    }),
    z.object({
        type: z.literal("read_receipt"),
        data: z.object({
            channelId: z.string().uuid(),
            userEmail: z.string().email(),
            lastReadAt: z.string().datetime(),
        }),
    }),
]);
//# sourceMappingURL=messages.js.map